import { useMemo, useState } from "react";
import { PatientProviderRowWithProvider } from "#/api/patientProvider.type";
import { ProviderRow } from "#/api/provider.type";
import { useLookupProviderFax } from "#/hooks/useLookupProviderFax";
import { SubmitButton } from "../shared/buttons/submit-button";
import Card from "../shared/card";
import Typography from "../shared/typography";
import { formatPhoneNumberToHumanReadable } from "../utls/utils";
import { formatTimeStampToHumanReadableDate } from "../utls/time";

export type ProviderDetailSource = ProviderRow | PatientProviderRowWithProvider;

type ProviderDetailCardProps = {
  provider: ProviderDetailSource;
};

const getProviderRecord = (provider: ProviderDetailSource) => {
  if ((provider as PatientProviderRowWithProvider).provider) {
    return (provider as PatientProviderRowWithProvider).provider as ProviderRow;
  }
  return provider as ProviderRow;
};

const buildName = (provider: ProviderRow & { name?: string | null }) => {
  const pieces = [provider.firstName, provider.lastName].filter(Boolean);
  if (pieces.length) {
    return pieces.join(" ");
  }
  return provider.name ?? "Care provider";
};

const formatDate = (value?: string | null) => {
  if (!value) return undefined;
  try {
    return formatTimeStampToHumanReadableDate(value);
  } catch {
    return undefined;
  }
};

export function ProviderDetailCard({ provider }: ProviderDetailCardProps) {
  const providerRecord = getProviderRecord(provider);
  const linkRecord = provider as PatientProviderRowWithProvider;
  const institution = linkRecord.institution;
  const institutionCity = (institution as any)?.city;
  const institutionState = (institution as any)?.state;
  const { mutateAsync: lookupFax, isPending: isLookupLoading } =
    useLookupProviderFax();
  const [faxResult, setFaxResult] = useState<string | null>(null);
  const [hasLookedUpFax, setHasLookedUpFax] = useState(false);

  const lookupTarget = useMemo(() => {
    const fallbackProviderId =
      linkRecord.provider_id ?? linkRecord.provider?.id ?? null;
    const providerId = providerRecord?.id ?? fallbackProviderId ?? null;
    const institutionId =
      linkRecord.institution?.id ?? linkRecord.institution_id ?? null;

    return {
      providerId: providerId ?? undefined,
      institutionId: institutionId ?? undefined,
    };
  }, [linkRecord, providerRecord]);

  const canLookupFax = Boolean(
    lookupTarget.providerId || lookupTarget.institutionId
  );

  const handleLookupFax = async () => {
    if (!canLookupFax) return;
    setHasLookedUpFax(false);
    setFaxResult(null);
    try {
      const result = await lookupFax(lookupTarget);
      setFaxResult(result.fax ?? null);
      setHasLookedUpFax(true);
    } catch (error) {
      // Toast handled in hook
    }
  };

  const metadata = [
    { label: "Credentials", value: providerRecord.credentials },
    { label: "Specialty", value: providerRecord.specialization },
    {
      label: "Linked on",
      value: formatDate(
        linkRecord?.created_at ?? (providerRecord as any).createdAt
      ),
    },
  ];

  return (
    <Card variant="cream" elevation={1} className="max-w-4xl">
      <Card.Body className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <Typography variant="h3" className="text-terracotta-600">
              {buildName(providerRecord)}
            </Typography>
            {providerRecord.specialization && (
              <Typography variant="body1" className="text-brown-700">
                {providerRecord.specialization}
              </Typography>
            )}
          </div>
          {canLookupFax && (
            <SubmitButton
              type="button"
              color="beige"
              size="sm"
              hideArrow
              onClick={handleLookupFax}
              isLoading={isLookupLoading}
            >
              Lookup Fax
            </SubmitButton>
          )}
        </div>

        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          {metadata
            .filter((item) => Boolean(item.value))
            .map((item) => (
              <div key={item.label} className="contents">
                <Typography
                  variant="caption"
                  className="font-semibold uppercase tracking-wide text-brown-600"
                >
                  {item.label}
                </Typography>
                <Typography variant="body2" className="text-brown-900">
                  {item.value}
                </Typography>
              </div>
            ))}
        </dl>

        {institution && (
          <div className="space-y-1">
            <Typography variant="subtitle2" className="text-brown-800">
              Institution
            </Typography>
            <Typography variant="body2" className="text-brown-700">
              {institution.name}
            </Typography>
            {(institutionCity || institutionState) && (
              <Typography variant="body2" className="text-brown-600">
                {[institutionCity, institutionState].filter(Boolean).join(", ")}
              </Typography>
            )}
          </div>
        )}

        {hasLookedUpFax && (
          <div className="rounded-lg bg-cream-100 px-4 py-3">
            <Typography variant="subtitle2" className="text-brown-800">
              Fax lookup result
            </Typography>
            <Typography variant="body2" className="text-brown-700">
              {faxResult
                ? formatPhoneNumberToHumanReadable(faxResult)
                : "No fax number found for this provider."}
            </Typography>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default ProviderDetailCard;
